import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Loader2, Rocket } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import PageHeader from "../components/PageHeader";
import ServizioAnteprimaPanel from "../components/trova/ServizioAnteprimaPanel";
import TrovaPreviewDashboard from "../components/trova/TrovaPreviewDashboard";
import {
  canAccessTrovaOspiti,
  canAccessTrovaPersonale,
} from "../utils/trovaServiziAccess";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrovaServizioPage({ config }) {
  const { token, user } = useAuth();
  const [panelOpen, setPanelOpen] = useState(true);
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const isDark = config.theme === "personale";
  const canAccess =
    config.servizio === "ospiti"
      ? canAccessTrovaOspiti(user)
      : canAccessTrovaPersonale(user);

  const loadRequest = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/trova-servizi/${config.servizio}/accesso`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingRequest(data.richiesta || null);
    } catch {
      setExistingRequest(null);
    } finally {
      setLoading(false);
    }
  }, [token, config.servizio]);

  useEffect(() => {
    if (canAccess) loadRequest();
  }, [loadRequest, canAccess]);

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative min-h-screen">
      <PageHeader
        title={config.title}
        subtitle={config.subtitle}
        icon={config.icon}
        iconContainerClassName={
          isDark
            ? "bg-gradient-to-r from-violet-500 to-violet-700"
            : "bg-gradient-to-r from-teal-500 to-teal-700"
        }
        iconClassName="text-white"
        actions={
          <Badge className={`${isDark ? "bg-violet-600" : "bg-sky-500"} text-white`}>
            BETA
          </Badge>
        }
      />

      <TrovaPreviewDashboard config={config} />

      {!panelOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 px-4 w-full max-w-md">
          <div
            className={`rounded-2xl shadow-xl border px-4 py-3 w-full text-center ${
              isDark
                ? "bg-slate-900 text-white border-violet-500/40"
                : "bg-white text-slate-800 border-teal-200"
            }`}
          >
            <p className="text-sm font-medium">
              {config.title} è in <span className="font-bold">beta</span> — richiedi l&apos;accesso anticipato
            </p>
            <Button
              className={`mt-2 w-full ${
                isDark
                  ? "bg-violet-600 hover:bg-violet-500 text-white"
                  : "bg-teal-700 hover:bg-teal-600 text-white"
              }`}
              onClick={() => setPanelOpen(true)}
            >
              <Rocket className="w-4 h-4 mr-2" />
              Richiedi accesso anticipato
            </Button>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-hidden">
          <div
            className={`absolute inset-0 ${
              isDark ? "bg-slate-950/70 backdrop-blur-[2px]" : "bg-slate-900/45 backdrop-blur-[1px]"
            }`}
            onClick={() => setPanelOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-10 w-full max-w-[920px] flex justify-center">
            {loading ? (
              <div
                className={`rounded-2xl p-12 shadow-2xl ${
                  isDark ? "bg-slate-900" : "bg-white"
                }`}
              >
                <Loader2
                  className={`w-8 h-8 animate-spin ${
                    isDark ? "text-violet-400" : "text-teal-600"
                  }`}
                />
              </div>
            ) : (
              <ServizioAnteprimaPanel
                config={config}
                token={token}
                existingRequest={existingRequest}
                onSubmitted={(richiesta) => {
                  setExistingRequest(richiesta);
                }}
                onExplore={() => setPanelOpen(false)}
                onClose={() => setPanelOpen(false)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
